<script setup lang="ts">
// Add complete title with prefix and specify ogTitle
useSeoMeta({
  title: 'AERO.NOW • Terms of Service',
  ogTitle: 'AERO.NOW • Terms of Service',
})

const currentDate = new Date().toISOString().split('T')[0]
</script>

<template>
  <UContainer>
    <UPage>
      <ULandingSection icon="i-heroicons-document-text" class="py-6">
        <template #title>
          <h1 class="uppercase mb-1">Terms of Service</h1>
        </template>

        <div class="text-gray-500 dark:text-gray-400 mb-8 text-center -mt-16">
          Effective Date: {{ currentDate }}
        </div>

        <div class="prose prose-primary dark:prose-invert max-w-none">
          <section class="mb-6">
            <h2 class="text-xl font-semibold">1. Acceptance</h2>
            <p>
              By using or subscribing to AERO.NOW, you confirm that you are at
              least 13 years of age and agree to be bound by these Terms of
              Service and our
              <NuxtLink
                to="/privacy"
                class="text-primary-600 dark:text-primary-400 hover:underline"
              >
                Privacy Policy </NuxtLink
              >. If you do not agree with these terms, please do not use the
              service.
            </p>
          </section>

          <section class="mb-6">
            <h2 class="text-xl font-semibold">2. Privacy Policy</h2>
            <p>
              Your use of AERO.NOW is also governed by our
              <NuxtLink
                to="/privacy"
                class="text-primary-600 dark:text-primary-400 hover:underline"
              >
                Privacy Policy </NuxtLink
              >, which is incorporated by reference. It explains how we collect,
              use, and protect your data.
            </p>
            <p class="mt-2">
              We implement different data collection practices based on your
              geographic location in accordance with applicable regulations like
              GDPR. By using our service, you agree to the data collection
              practices appropriate for your region as outlined in our Privacy
              Policy.
            </p>
          </section>

          <section class="mb-6">
            <h2 class="text-xl font-semibold">3. Conduct</h2>
            <p>You agree not to:</p>
            <ul class="list-disc list-inside ml-4">
              <li>
                Use the newsletter or site to send spam, harass, or defame
                others
              </li>
              <li>Engage in illegal or disruptive behavior</li>
              <li>
                Circumvent any access controls or interfere with system
                operations
              </li>
            </ul>
          </section>

          <section class="mb-6">
            <h2 class="text-xl font-semibold">4. Intellectual Property</h2>
            <p>
              All content published via AERO.NOW is owned by us or our
              licensors. You may not copy, republish, or redistribute any
              content without prior written permission.
            </p>
          </section>

          <section class="mb-6">
            <h2 class="text-xl font-semibold">5. Submissions</h2>
            <p>
              If you submit feedback or content (e.g., testimonials), you grant
              AERO.NOW a non-exclusive, royalty-free license to use it. You
              retain copyright over your submissions unless otherwise agreed.
            </p>
          </section>

          <section class="mb-6">
            <h2 class="text-xl font-semibold">6. Disclaimer</h2>
            <p>
              The content provided by AERO.NOW is for informational purposes
              only and should not be considered investment, legal, medical, or
              professional advice. Use it at your own risk.
            </p>
            <p class="mt-2">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, AERO.NOW DISCLAIMS ALL
              WARRANTIES, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO
              IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
              PURPOSE, AND NON-INFRINGEMENT.
            </p>
          </section>

          <section class="mb-6">
            <h2 class="text-xl font-semibold">7. Limitation of Liability</h2>
            <p>
              TO THE FULLEST EXTENT PERMITTED BY LAW, IN NO EVENT SHALL
              AERO.NOW, ITS DIRECTORS, EMPLOYEES, OR AGENTS BE LIABLE FOR ANY
              INDIRECT, PUNITIVE, INCIDENTAL, SPECIAL, OR CONSEQUENTIAL DAMAGES
              ARISING OUT OF OR IN ANY WAY CONNECTED WITH THE USE OF OUR
              SERVICES, INCLUDING WITHOUT LIMITATION LOSS OF REVENUE, PROFITS,
              OR DATA.
            </p>
          </section>

          <section class="mb-6">
            <h2 class="text-xl font-semibold">8. Termination</h2>
            <p>
              We reserve the right to suspend or terminate access to our service
              at our discretion, including if you breach these terms. Upon
              termination, your right to use the service will immediately cease.
            </p>
          </section>

          <section class="mb-6">
            <h2 class="text-xl font-semibold">9. Changes to Terms</h2>
            <p>
              We may update these Terms of Service from time to time. We will
              notify you of any changes by posting the new Terms on this page
              and updating the "Effective Date" at the top. You are advised to
              review these Terms periodically for any changes.
            </p>
          </section>

          <section class="mb-6">
            <h2 class="text-xl font-semibold">10. Governing Law</h2>
            <p>
              These Terms are governed by applicable laws of your country of
              residence, unless otherwise required by overriding consumer
              protection laws. Disputes will be handled in a mutually agreed
              competent jurisdiction.
            </p>
          </section>

          <section class="mb-6">
            <h2 class="text-xl font-semibold">11. Severability</h2>
            <p>
              If any provision of these Terms is held to be unenforceable or
              invalid, such provision will be changed and interpreted to
              accomplish the objectives of such provision to the greatest extent
              possible under applicable law, and the remaining provisions will
              continue in full force and effect.
            </p>
          </section>

          <section class="mb-6">
            <h2 class="text-xl font-semibold">12. Contact</h2>
            <p>
              For questions about these Terms, please contact us at
              <a
                href="mailto:bart@aero.now"
                class="text-primary-600 dark:text-primary-400 hover:underline"
              >
                bart@aero.now</a
              >.
            </p>
          </section>
        </div>
      </ULandingSection>
    </UPage>
  </UContainer>
</template>
